import React, { useState, useEffect } from 'react';
import { array, bool, number } from 'prop-types';
import { isEmpty } from 'lodash';

import MaterialTable from 'material-table';

import { TablePagination } from '@material-ui/core';

import ActionBar from '../../../components/ActionBar';
import ActionBarItem from '../../../components/ActionBarItem';
import Switch from '../../../components/FormFields/Switch';

import classes from './Table.module.css';

const ClientSettingsTable = ({
    clientSettings,
    clientSettingsMeta,
    isLoading,
    setClientSettingsSearch,
    onPageSet,
    onPageSizeSet,
    page,
    pageSize,
    updateClientSettings,
    ...props
}) => {

    const [selectedClients, setSelectedClients] = useState([]);
    const [clientToggles, setClientToggles] = useState({});
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        clientSettings.forEach(client => {
            setClientToggles({...clientToggles, [client.id]: false});
        });
    }, [clientSettings]);

    useEffect(() => {
        if (clientSettingsMeta.totalCount && clientSettingsMeta.totalCount !== totalCount) {
            setTotalCount(clientSettingsMeta.totalCount);
        }
    }, [clientSettingsMeta]);

    const enabledSorter = (a, b) => {
        const valueA = a.clientEnabled;
        const valueB = b.clientEnabled;

        return (valueA === valueB) ? 0 : valueA ? -1 : 1;
    };

    const handlePageChange = (event, page) => {
        onPageSet(page);
    };

    const handlePageSizeChange = event => {
        const { value } = event.target;

        onPageSizeSet(value);
    };

    const handleSearch = searchValue => {
        setClientSettingsSearch(searchValue);
    };

    const handleCellToggle = async rowData => {
        setClientToggles({...clientToggles, [rowData.id]: true});
        await updateClientSettings(!rowData.clientEnabled, rowData);
        setClientToggles({...clientToggles, [rowData.id]: false});
    };

    const handleSetSelectedClientSettings = selected => {
        setSelectedClients(selected);
    };

    const handleEnable = () => {
        if (isEmpty(selectedClients)) {
            return;
        }

        const newIds = selectedClients.map(client => client.id);

        const toggles = {};
        newIds.forEach(id => {
            toggles[id] = true;
        });
        setClientToggles({...clientToggles, ...toggles});

        const promises = [];
        selectedClients.forEach(client => {
            promises.push(updateClientSettings(true, client));
        });

        Promise.all(promises).then(() => {
            newIds.forEach(id => {
                toggles[id] = false;
            });
            setClientToggles({...clientToggles, ...toggles});
        });
    };

    const handleDisable = async () => {
        if (isEmpty(selectedClients)) {
            return;
        }

        const newIds = selectedClients.map(client => client.id);

        const toggles = {};
        newIds.forEach(id => {
            toggles[id] = true;
        });
        setClientToggles({...clientToggles, ...toggles});

        const promises = [];
        selectedClients.forEach(client => {
            promises.push(updateClientSettings(false, client));
        });

        Promise.all(promises).then(() => {
            newIds.forEach(id => {
                toggles[id] = false;
            });
            setClientToggles({...clientToggles, ...toggles});
        });
    };

    const renderToggleCell = (rowData, value) => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        return (
            <Switch
                checked={rowData[value] || false}
                disableRipple
                name="enabled"
                onChange={() => handleCellToggle(rowData)}
                isLoading={clientToggles[rowData.id]}
            />
        );
    };

    return (
        <React.Fragment>
            <div className={classes.Table} >
                <ActionBar
                    onSearch={handleSearch}
                >
                    <ActionBarItem
                        onClick={handleEnable}
                        iconSrc="/assets/images/switchEnabledIconColor.svg"
                    >
                        Enabled
                    </ActionBarItem>
                    <ActionBarItem
                        onClick={handleDisable}
                        iconSrc="/assets/images/switchDisabledIconColor.svg"
                    >
                        Disabled
                    </ActionBarItem>
                </ActionBar>
                <MaterialTable
                    options={{
                        paging:              true,
                        showTitle:           false,
                        search:              false,
                        selection:           true,
                        sorting:             true,
                        toolbar:             false,
                        emptyRowsWhenPaging: false,
                        pageSize
                    }}
                    columns={[
                        {
                            title: 'Client Name',
                            field: 'name'
                        },
                        {
                            title:      'Enabled',
                            render:     rowData => renderToggleCell(rowData, 'clientEnabled'),
                            customSort: enabledSorter
                        }
                    ]}
                    data={clientSettings}
                    isLoading={isLoading}
                    onSelectionChange={handleSetSelectedClientSettings}
                    components={{
                        Pagination: props => {
                            return (
                                <TablePagination
                                    {...props}
                                    count={totalCount}
                                    page={clientSettings.length ? page : 0}
                                    rowsPerPage={pageSize}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={event => { props.onChangeRowsPerPage(event); handlePageSizeChange(event); } }
                                />
                            );
                        }
                    }}
                />
            </div>
        </React.Fragment>
    );
};

ClientSettingsTable.defaultProps = {
    clientSettings:        [],
    clientSettingsLoading: false,
    page:                  0,
    pageSize:              100
};

ClientSettingsTable.propTypes = {
    clientSettings:        array.isRequired,
    clientSettingsLoading: bool,
    page:                  number,
    pageSize:              number
};

export default ClientSettingsTable;
